import { createDemoSnapshot } from "./data";
import { DEFAULT_SPOT_ID, isSpotId } from "./spots";
import {
  enqueueReport,
  getAnonymousDeviceId,
  getPendingReports,
  removePendingReport,
} from "./storage";
import type {
  DataMode,
  DataSnapshot,
  CapabilityUnavailableReason,
  OperationsEstimate,
  OperationsRiskAreaProperties,
  PendingReport,
  PublicCapabilities,
  PublicCapabilityName,
  ReportPhotoDraft,
  ReportPhotoUploadStatus,
  ReportSubmissionResult,
  Scenario,
  SpotId,
  ToiletPhoto,
} from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const JSON_HEADERS = { "content-type": "application/json" };
const SNAPSHOT_TIMEOUT_MS = 6_000;
const CAPABILITY_NAMES = [
  "communityReporting",
  "photoUpload",
  "routing",
] as const;
const CAPABILITY_UNAVAILABLE_REASONS = new Set<CapabilityUnavailableReason>([
  "community_reporting_disabled",
  "reporting_not_configured",
  "photo_storage_not_ready",
  "photo_reporting_not_configured",
  "routing_not_configured",
]);
const CAPABILITY_REASON_MESSAGES: Record<CapabilityUnavailableReason, string> = {
  community_reporting_disabled: "運用設定により、現場からの利用状況報告は停止中です。",
  reporting_not_configured: "利用状況報告の保存先が未設定です。",
  photo_storage_not_ready: "写真の保存先を準備中のため、写真投稿は利用できません。",
  photo_reporting_not_configured: "写真投稿の受付設定が未完了です。",
  routing_not_configured: "徒歩経路サービスが未設定のため、経路案内は利用できません。",
  capabilities_unavailable: "機能の利用可否を確認できないため、安全のため停止しています。",
};
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

class ApiHttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiHttpError";
  }
}

const shouldQueueReportFailure = (error: unknown): boolean => {
  if (error instanceof TypeError) return true;
  if (error instanceof Error && error.name === "AbortError") return true;
  if (!(error instanceof ApiHttpError)) return false;
  if (
    error.code &&
    CAPABILITY_UNAVAILABLE_REASONS.has(error.code as CapabilityUnavailableReason)
  ) {
    return false;
  }
  return error.status === 408 || error.status === 429 || error.status >= 500;
};

const withTimeout = (milliseconds: number, externalSignal?: AbortSignal) => {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(() => controller.abort(), milliseconds);
  const abortFromExternal = () => controller.abort(externalSignal?.reason);
  if (externalSignal?.aborted) abortFromExternal();
  else externalSignal?.addEventListener("abort", abortFromExternal, { once: true });
  return {
    signal: controller.signal,
    clear: () => {
      globalThis.clearTimeout(timer);
      externalSignal?.removeEventListener("abort", abortFromExternal);
    },
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isNonNegativeNumber = (value: unknown): value is number =>
  isFiniteNumber(value) && value >= 0;

const isNonNegativeInteger = (value: unknown): value is number =>
  Number.isInteger(value) && isNonNegativeNumber(value);

const isConfidence = (value: unknown): value is number =>
  isFiniteNumber(value) && value >= 0 && value <= 1;

const isIsoDate = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && Number.isFinite(Date.parse(value));

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const unavailableCapabilities = (): PublicCapabilities => ({
  schemaVersion: 1,
  communityReporting: false,
  photoUpload: false,
  routing: false,
  reasons: {
    communityReporting: "capabilities_unavailable",
    photoUpload: "capabilities_unavailable",
    routing: "capabilities_unavailable",
  },
});

export const formatCapabilityMessages = (
  capabilities: PublicCapabilities,
): Partial<Record<PublicCapabilityName, string>> => {
  const messages: Partial<Record<PublicCapabilityName, string>> = {};
  for (const name of CAPABILITY_NAMES) {
    if (capabilities[name]) continue;
    const reason = capabilities.reasons[name] ?? "capabilities_unavailable";
    messages[name] = CAPABILITY_REASON_MESSAGES[reason];
  }
  return messages;
};

const isPublicCapabilities = (value: unknown): value is PublicCapabilities => {
  if (!isRecord(value) || value.schemaVersion !== 1 || !isRecord(value.reasons)) {
    return false;
  }
  const reasons = value.reasons;
  const allowedTopLevelKeys = new Set([
    "schemaVersion",
    "communityReporting",
    "photoUpload",
    "routing",
    "reasons",
  ]);
  if (
    Object.keys(value).length !== allowedTopLevelKeys.size ||
    Object.keys(value).some((key) => !allowedTopLevelKeys.has(key))
  ) {
    return false;
  }
  if (
    Object.keys(reasons).some(
      (key) => !CAPABILITY_NAMES.includes(key as (typeof CAPABILITY_NAMES)[number]),
    )
  ) {
    return false;
  }
  return CAPABILITY_NAMES.every((name) => {
    const available = value[name];
    const reason = reasons[name];
    return (
      typeof available === "boolean" &&
      (available
        ? reason === undefined
        : typeof reason === "string" &&
          CAPABILITY_UNAVAILABLE_REASONS.has(reason as CapabilityUnavailableReason))
    );
  });
};

const isPosition = (value: unknown): value is [number, number, ...number[]] =>
  Array.isArray(value) &&
  value.length >= 2 &&
  value.every(isFiniteNumber) &&
  value[0]! >= -180 &&
  value[0]! <= 180 &&
  value[1]! >= -90 &&
  value[1]! <= 90;

const isLinearRing = (value: unknown) => {
  if (!Array.isArray(value) || value.length < 4 || !value.every(isPosition)) {
    return false;
  }
  const first = value[0]!;
  const last = value[value.length - 1]!;
  return first[0] === last[0] && first[1] === last[1];
};

const isPolygonCoordinates = (value: unknown) =>
  Array.isArray(value) && value.length > 0 && value.every(isLinearRing);

const isRiskGeometry = (value: unknown) => {
  if (!isRecord(value)) return false;
  if (value.type === "Polygon") return isPolygonCoordinates(value.coordinates);
  return (
    value.type === "MultiPolygon" &&
    Array.isArray(value.coordinates) &&
    value.coordinates.length > 0 &&
    value.coordinates.every(isPolygonCoordinates)
  );
};

const hasValidInterval = (
  value: Record<string, unknown>,
  lowerKey: string,
  pointKey: string,
  upperKey: string,
) => {
  const lower = value[lowerKey];
  const point = value[pointKey];
  const upper = value[upperKey];
  if (lower === null && point === null && upper === null) return true;
  return (
    isNonNegativeNumber(lower) &&
    isNonNegativeNumber(point) &&
    isNonNegativeNumber(upper) &&
    lower <= point &&
    point <= upper
  );
};

const isRiskProperties = (
  value: unknown,
): value is OperationsRiskAreaProperties => {
  if (!isRecord(value)) return false;
  const isKnownRiskLevel = ["adequate", "strained", "critical"].includes(
    String(value.riskLevel),
  );
  return (
    typeof value.areaId === "string" &&
    value.areaId.length > 0 &&
    (isKnownRiskLevel || value.riskLevel === "unknown") &&
    (typeof value.problemLikely === "boolean" ||
      value.problemLikely === null) &&
    (value.riskLevel !== "unknown" || value.problemLikely === null) &&
    hasValidInterval(value, "demandLower", "demandPoint", "demandUpper") &&
    hasValidInterval(value, "supplyLower", "supplyPoint", "supplyUpper") &&
    hasValidInterval(value, "pressureLower", "pressurePoint", "pressureUpper") &&
    (!isKnownRiskLevel ||
      (value.demandPoint !== null &&
        value.supplyPoint !== null &&
        value.pressurePoint !== null)) &&
    isConfidence(value.confidence) &&
    typeof value.method === "string" &&
    value.method.length > 0 &&
    isNonNegativeInteger(value.facilityCount) &&
    isNonNegativeInteger(value.knownCapacityCount) &&
    isNonNegativeInteger(value.unknownCapacityCount) &&
    isNonNegativeInteger(value.historicalEventCount) &&
    isNonNegativeInteger(value.historicalHazardCount) &&
    typeof value.summary === "string" &&
    value.summary.length > 0 &&
    isStringArray(value.sourceIds) &&
    isStringArray(value.limitations) &&
    value.isSimulation === true
  );
};

const isOperationsEstimate = (value: unknown): value is OperationsEstimate => {
  if (!isRecord(value) || !isRecord(value.model) || !isRecord(value.summary)) {
    return false;
  }
  if (
    !isRecord(value.riskAreas) ||
    value.riskAreas.type !== "FeatureCollection" ||
    !Array.isArray(value.riskAreas.features) ||
    value.riskAreas.features.length > 400
  ) {
    return false;
  }
  const featuresAreValid = value.riskAreas.features.every(
    (feature) =>
      isRecord(feature) &&
      feature.type === "Feature" &&
      isRiskGeometry(feature.geometry) &&
      isRiskProperties(feature.properties),
  );
  const features = featuresAreValid
    ? value.riskAreas.features as Array<{
        properties: OperationsRiskAreaProperties;
      }>
    : [];
  const actualRiskCounts = features.reduce(
    (counts, feature) => {
      counts[feature.properties.riskLevel] += 1;
      if (feature.properties.problemLikely === true) counts.problemLikely += 1;
      return counts;
    },
    {
      adequate: 0,
      strained: 0,
      critical: 0,
      unknown: 0,
      problemLikely: 0,
    },
  );
  const areaIds = features.map((feature) => feature.properties.areaId);
  const summary = value.summary;
  const areaCounts = [
    summary.adequateAreas,
    summary.strainedAreas,
    summary.criticalAreas,
    summary.unknownAreas,
  ];
  return (
    value.schemaVersion === 1 &&
    ["ready", "partial", "insufficient_data"].includes(String(value.status)) &&
    value.isSimulation === true &&
    typeof value.spotId === "string" &&
    isSpotId(value.spotId) &&
    ["normal", "event", "outage", "earthquake"].includes(String(value.scenario)) &&
    isIsoDate(value.requestedAt) &&
    isIsoDate(value.generatedAt) &&
    (value.dataCutoffAt === null || isIsoDate(value.dataCutoffAt)) &&
    typeof value.model.version === "string" &&
    value.model.version.length > 0 &&
    typeof value.model.demandMethod === "string" &&
    value.model.demandMethod.length > 0 &&
    typeof value.model.supplyMethod === "string" &&
    value.model.supplyMethod.length > 0 &&
    typeof value.model.riskMethod === "string" &&
    value.model.riskMethod.length > 0 &&
    (value.model.trainingCutoff === null || isIsoDate(value.model.trainingCutoff)) &&
    isNonNegativeInteger(summary.areaCount) &&
    areaCounts.every(isNonNegativeInteger) &&
    areaCounts.reduce((total, count) => total + count, 0) === summary.areaCount &&
    isNonNegativeInteger(summary.problemLikelyAreas) &&
    summary.problemLikelyAreas <= summary.areaCount &&
    isConfidence(summary.confidence) &&
    featuresAreValid &&
    new Set(areaIds).size === areaIds.length &&
    value.riskAreas.features.length === summary.areaCount &&
    actualRiskCounts.adequate === summary.adequateAreas &&
    actualRiskCounts.strained === summary.strainedAreas &&
    actualRiskCounts.critical === summary.criticalAreas &&
    actualRiskCounts.unknown === summary.unknownAreas &&
    actualRiskCounts.problemLikely === summary.problemLikelyAreas &&
    isStringArray(value.limitations)
  );
};

const isSnapshot = (value: unknown): value is DataSnapshot => {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<DataSnapshot>;
  return (
    typeof candidate.region?.spotId === "string" &&
    isSpotId(candidate.region.spotId) &&
    typeof candidate.region.municipalityCode === "string" &&
    /^13\d{3}$/.test(candidate.region.municipalityCode) &&
    Array.isArray(candidate.toilets) &&
    Array.isArray(candidate.hazards) &&
    Array.isArray(candidate.sources)
  );
};

/**
 * Reads deployment-level public feature gates. A missing, unreachable, or
 * malformed endpoint always fails closed so the UI cannot advertise a write
 * or navigation action that the Worker cannot complete.
 */
export async function fetchCapabilities(
  options: { signal?: AbortSignal } = {},
): Promise<PublicCapabilities> {
  const timeout = withTimeout(3_000, options.signal);
  try {
    const response = await fetch(`${API_BASE}/api/v1/capabilities`, {
      headers: { accept: "application/json" },
      signal: timeout.signal,
    });
    if (
      !response.ok ||
      !response.headers.get("content-type")?.includes("application/json")
    ) {
      return unavailableCapabilities();
    }
    const payload: unknown = await response.json();
    return isPublicCapabilities(payload) ? payload : unavailableCapabilities();
  } catch {
    return unavailableCapabilities();
  } finally {
    timeout.clear();
  }
}

export async function fetchSnapshot(
  spotId: SpotId = DEFAULT_SPOT_ID,
  options: { signal?: AbortSignal; fallbackToDemo?: boolean } = {},
): Promise<{
  snapshot: DataSnapshot;
  source: Exclude<DataMode, "saved">;
}> {
  const timeout = withTimeout(SNAPSHOT_TIMEOUT_MS, options.signal);
  try {
    const response = await fetch(
      `${API_BASE}/api/v1/offline-pack?spot=${encodeURIComponent(spotId)}&version=latest`,
      {
        headers: { accept: "application/json" },
        signal: timeout.signal,
      },
    );
    if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) {
      throw new Error(`offline_pack_${response.status}`);
    }
    const payload: unknown = await response.json();
    const snapshot =
      payload &&
      typeof payload === "object" &&
      "snapshot" in payload &&
      isSnapshot((payload as { snapshot?: unknown }).snapshot)
        ? (payload as { snapshot: DataSnapshot }).snapshot
        : payload;
    if (!isSnapshot(snapshot)) throw new Error("invalid_offline_pack");
    const responseMode = response.headers.get("x-data-mode");
    return {
      snapshot,
      source: responseMode === "supabase" ? "api" : "api_demo",
    };
  } catch (error) {
    if (options.fallbackToDemo === false) throw error;
    return { snapshot: createDemoSnapshot(new Date(), spotId), source: "demo" };
  } finally {
    timeout.clear();
  }
}

export async function fetchOperationsEstimate(
  spotId: SpotId,
  scenario: Scenario,
  at: Date,
  options: { signal?: AbortSignal } = {},
): Promise<OperationsEstimate> {
  if (!Number.isFinite(at.getTime())) throw new RangeError("invalid_operations_time");
  const timeout = withTimeout(10_000, options.signal);
  try {
    const response = await fetch(`${API_BASE}/api/v1/operations/estimate`, {
      method: "POST",
      headers: { ...JSON_HEADERS, accept: "application/json" },
      signal: timeout.signal,
      body: JSON.stringify({
        spot: spotId,
        scenario,
        at: at.toISOString(),
      }),
    });
    if (
      !response.ok ||
      !response.headers.get("content-type")?.includes("application/json")
    ) {
      throw new Error(`operations_estimate_${response.status}`);
    }
    const payload: unknown = await response.json();
    if (
      !isOperationsEstimate(payload) ||
      payload.spotId !== spotId ||
      payload.scenario !== scenario ||
      Date.parse(payload.requestedAt) !== at.getTime()
    ) {
      throw new Error("invalid_operations_estimate");
    }
    return payload;
  } finally {
    timeout.clear();
  }
}

type ReportReceipt = {
  persisted: boolean;
  reportId?: string;
};

async function postReport(report: PendingReport): Promise<ReportReceipt> {
  const timeout = withTimeout(10_000);
  try {
    const response = await fetch(`${API_BASE}/api/v1/reports`, {
      method: "POST",
      headers: {
        ...JSON_HEADERS,
        accept: "application/json",
        "x-anonymous-device-id": report.deviceId,
      },
      body: JSON.stringify({
        toiletId: report.toiletId,
        status: report.status,
        queueBand: report.queueBand,
        reportedAt: report.reportedAt,
      }),
      signal: timeout.signal,
    });
    if (!response.ok) {
      let errorCode: string | undefined;
      if (response.headers.get("content-type")?.includes("application/json")) {
        try {
          const payload: unknown = await response.json();
          if (
            isRecord(payload) &&
            isRecord(payload.error) &&
            typeof payload.error.code === "string"
          ) {
            errorCode = payload.error.code;
          }
        } catch {
          // Keep the HTTP status as the fallback classification.
        }
      }
      throw new ApiHttpError(
        response.status,
        `report_${response.status}`,
        errorCode,
      );
    }
    if (!response.headers.get("content-type")?.includes("application/json")) {
      throw new Error("invalid_report_content_type");
    }
    const payload: unknown = await response.json();
    if (!isRecord(payload) || payload.accepted !== true) {
      throw new Error("report_not_accepted");
    }
    if (typeof payload.persisted !== "boolean") {
      throw new Error("invalid_report_receipt");
    }
    const rawReportId = payload.reportId;
    const reportId = rawReportId === null ? undefined : rawReportId;
    if (
      reportId !== undefined &&
      (typeof reportId !== "string" || !/^[1-9][0-9]*$/.test(reportId))
    ) {
      throw new Error("invalid_report_id");
    }
    if (payload.persisted && reportId === undefined) throw new Error("missing_report_id");
    return { persisted: payload.persisted, reportId };
  } finally {
    timeout.clear();
  }
}

async function postReportPhoto(
  reportId: string,
  photoId: string,
  draft: ReportPhotoDraft,
  deviceId: string,
): Promise<string> {
  const search = new URLSearchParams({
    reportId,
    photoId,
    kind: draft.kind,
  });
  const timeout = withTimeout(20_000);
  try {
    const response = await fetch(`${API_BASE}/api/v1/report-photo?${search}`, {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": draft.file.type,
        "x-anonymous-device-id": deviceId,
      },
      body: draft.file,
      signal: timeout.signal,
    });
    if (
      !response.ok ||
      !response.headers.get("content-type")?.includes("application/json")
    ) {
      throw new Error(`report_photo_${response.status}`);
    }
    const payload: unknown = await response.json();
    if (
      !isRecord(payload) ||
      payload.accepted !== true ||
      payload.moderationStatus !== "pending" ||
      payload.photoId !== photoId
    ) {
      throw new Error("report_photo_not_accepted");
    }
    return payload.photoId;
  } finally {
    timeout.clear();
  }
}

export async function submitReport(
  input: Omit<PendingReport, "id" | "deviceId" | "reportedAt">,
  photo?: ReportPhotoDraft,
): Promise<ReportSubmissionResult> {
  const report: PendingReport = {
    ...input,
    id: crypto.randomUUID(),
    deviceId: await getAnonymousDeviceId(),
    reportedAt: new Date().toISOString(),
  };
  let receipt: ReportReceipt;
  try {
    receipt = await postReport(report);
  } catch (error) {
    if (!shouldQueueReportFailure(error)) throw error;
    await enqueueReport(report);
    return {
      report,
      queued: true,
      photoStatus: photo ? "not_uploaded" : undefined,
    };
  }

  let photoStatus: ReportPhotoUploadStatus | undefined;
  let photoId: string | undefined;
  if (photo) {
    if (!receipt.persisted || !receipt.reportId) {
      photoStatus = "local";
    } else {
      photoId = crypto.randomUUID();
      try {
        await postReportPhoto(receipt.reportId, photoId, photo, report.deviceId);
        photoStatus = "uploaded";
      } catch {
        photoStatus = "not_uploaded";
      }
    }
  }
  return { report, queued: false, photoStatus, photoId };
}

const isPhotoKind = (value: unknown): value is ToiletPhoto["kind"] =>
  value === "location" || value === "exterior";

const resolvePhotoUrl = (
  value: unknown,
  photoId: string,
  variant: "thumbnail" | "full",
): string | undefined => {
  if (typeof value !== "string" || value.length < 1 || value.length > 2_048) return undefined;
  const pageOrigin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  let endpoint: URL;
  let parsed: URL;
  try {
    endpoint = new URL(`${API_BASE || pageOrigin}/api/v1/toilet-photo`, pageOrigin);
    parsed = new URL(value, endpoint.origin);
  } catch {
    return undefined;
  }
  if (
    parsed.origin !== endpoint.origin ||
    parsed.pathname !== endpoint.pathname ||
    parsed.hash !== "" ||
    parsed.searchParams.size !== 2 ||
    parsed.searchParams.getAll("photoId").length !== 1 ||
    parsed.searchParams.get("photoId") !== photoId ||
    parsed.searchParams.getAll("variant").length !== 1 ||
    parsed.searchParams.get("variant") !== variant
  ) {
    return undefined;
  }
  return value.startsWith("/") ? `${API_BASE}${value}` : value;
};

export async function fetchToiletPhotos(
  toiletId: string,
  options: { signal?: AbortSignal } = {},
): Promise<ToiletPhoto[]> {
  const search = new URLSearchParams({ toiletId });
  const timeout = withTimeout(8_000, options.signal);
  try {
    const response = await fetch(`${API_BASE}/api/v1/toilet-photos?${search}`, {
      headers: { accept: "application/json" },
      signal: timeout.signal,
    });
    if (
      !response.ok ||
      !response.headers.get("content-type")?.includes("application/json")
    ) {
      throw new Error(`toilet_photos_${response.status}`);
    }
    const payload: unknown = await response.json();
    const photos = isRecord(payload) ? payload.photos : undefined;
    if (
      !isRecord(payload) ||
      payload.toiletId !== toiletId ||
      !Array.isArray(photos) ||
      photos.length > 20
    ) {
      throw new Error("invalid_toilet_photos");
    }
    const seenPhotoIds = new Set<string>();
    return photos.map((value): ToiletPhoto => {
      const id = isRecord(value) && typeof value.id === "string" ? value.id : "";
      const thumbnailUrl = isRecord(value)
        ? resolvePhotoUrl(value.thumbnailUrl, id, "thumbnail")
        : undefined;
      const fullUrl = isRecord(value)
        ? resolvePhotoUrl(value.fullUrl, id, "full")
        : undefined;
      if (
        !isRecord(value) ||
        !UUID_PATTERN.test(id) ||
        seenPhotoIds.has(id) ||
        !isPhotoKind(value.kind) ||
        !thumbnailUrl ||
        !fullUrl ||
        !isIsoDate(value.submittedAt)
      ) {
        throw new Error("invalid_toilet_photo");
      }
      seenPhotoIds.add(id);
      return {
        id,
        toiletId,
        kind: value.kind,
        thumbnailUrl,
        fullUrl,
        submittedAt: value.submittedAt,
        status: "approved",
      };
    });
  } finally {
    timeout.clear();
  }
}

export async function flushPendingReports(): Promise<{
  sent: number;
  remaining: number;
  /** Present when this client discarded reports that can never be retried. */
  discarded?: number;
}> {
  const pending = await getPendingReports();
  let sent = 0;
  let discarded = 0;
  for (const report of pending) {
    let receipt: ReportReceipt;
    try {
      receipt = await postReport(report);
    } catch (error) {
      if (shouldQueueReportFailure(error)) break;
      await removePendingReport(report.id);
      discarded += 1;
      continue;
    }

    // A demo or otherwise non-persisting endpoint acknowledged the request but
    // did not store it. Keep this report (and FIFO successors) for a deployment
    // that can confirm persistence instead of silently losing the observation.
    if (!receipt.persisted) break;

    await removePendingReport(report.id);
    sent += 1;
  }
  return { sent, remaining: (await getPendingReports()).length, discarded };
}
